import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Responsive as ResponsiveBase } from "react-grid-layout";
import { useMediaQuery } from "react-responsive";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import Window from "./Window";
import PiChart from "../Pi/PiChart";
import PiChart2 from "../Pi/PiChart2";
import ColumnChart1 from "../Column/ColumnChart1";
import DualAxesChart from "../DualAxes/DualAxesChart";
import DualAxesChart2 from "../DualAxes/DualAxesChart2";
import FunnelChart from "../Funnel/FunnelChart";
import RadarChart from "../Radar/RadarChart";
import TreemapChart from "../Treemap/TreemapChart";
import GaugeChart from "../Gauge/GaugeChart";
import BaseChart from "../Base/BaseChart";
import { sampleReport } from "../Layout/sampleReport";

/** ========= تایپ‌های مستقل (برای دور زدن ناقصی @types) ========= */
type Breakpoint = "lg" | "md" | "sm";
type CompactType = "vertical" | "horizontal" | null;
type ResizeHandle = "s" | "w" | "e" | "n" | "sw" | "nw" | "se" | "ne";

type RGLLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
};

type RGLLayout = RGLLayoutItem[];
type RGLLayouts = Record<string, RGLLayout>;

type ResponsiveRGLProps = {
  width: number;
  className?: string;
  layouts: RGLLayouts;
  breakpoints: Record<string, number>;
  cols: Record<string, number>;
  rowHeight?: number;
  draggableCancel?: string;

  onLayoutChange?: (currentLayout: RGLLayout, allLayouts: RGLLayouts) => void;

  // ✅ اینا رو تایپ می‌دیم که TS گیر نده
  isDraggable?: boolean;
  isResizable?: boolean;
  preventCollision?: boolean;
  compactType?: CompactType;
  draggableHandle?: string;
  resizeHandles?: ResizeHandle[];

  // ✅ این دو تا برای ذخیره “قطعی”
  onDragStop?: (...args: any[]) => void;
  onResizeStop?: (...args: any[]) => void;

  children?: React.ReactNode;
};

const ResponsiveRGL = ResponsiveBase as unknown as React.ComponentType<ResponsiveRGLProps>;

/** ========= تنظیمات ========= */
const breakpoints = { lg: 1200, md: 996, sm: 768 };
const cols = { lg: 12, md: 10, sm: 1 };

type Widget = {
  id: string; // unique + پایدار
  title: any ;
  content: any ;
  defaultW?: number;
  defaultH?: number;
  minW?: number;
  minH?: number;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

/** ========= Width از کانتینر ========= */
function useContainerWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(1200);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const w = el.getBoundingClientRect().width;
      setWidth(w > 0 ? w : window.innerWidth);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, width };
}

/** ========= LocalStorage helpers ========= */
function safeRead(key: string): RGLLayouts | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as RGLLayouts) : null;
  } catch {
    return null;
  }
}
function safeWrite(key: string, value: RGLLayouts) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

/** ========= Auto layout ========= */
function autoLayoutFor(bp: Breakpoint, widgets: Widget[]): RGLLayout {
  const C = cols[bp];

  const baseW = bp === "sm" ? 1 : bp === "md" ? 5 : 6;
  const baseH = 4;

  let x = 0;
  let y = 0;
  let rowH = baseH;

  const layout: RGLLayout = [];

  for (const wdg of widgets) {
    const w = clamp(wdg.defaultW ?? baseW, 1, C);
    const h = wdg.defaultH ?? baseH;

    if (x + w > C) {
      x = 0;
      y += rowH;
      rowH = baseH;
    }

    layout.push({
      i: wdg.id,
      x,
      y,
      w,
      h,
      minW: clamp(wdg.minW ?? 1, 1, C),
      minH: wdg.minH ?? 2,
    });

    x += w;
    rowH = Math.max(rowH, h);
  }

  return layout;
}

function buildDefaults(widgets: Widget[]): RGLLayouts {
  return {
    lg: autoLayoutFor("lg", widgets),
    md: autoLayoutFor("md", widgets),
    sm: autoLayoutFor("sm", widgets),
  };
}

function mergeLayouts(saved: RGLLayouts | null, defaults: RGLLayouts, widgets: Widget[]): RGLLayouts {
  if (!saved) return defaults;

  const ids = new Set(widgets.map((w) => w.id));
  const out: RGLLayouts = { lg: [], md: [], sm: [] };

  (Object.keys(cols) as Breakpoint[]).forEach((bp) => {
    const C = cols[bp];

    const s = (saved[bp] ?? []).filter((it) => ids.has(it.i));
    const inSaved = new Set(s.map((it) => it.i));
    const missing = (defaults[bp] ?? []).filter((it) => !inSaved.has(it.i));

    out[bp] = [...s, ...missing].map((it) => ({
      ...it,
      w: clamp(it.w, 1, C),
      x: clamp(it.x, 0, Math.max(0, C - 1)),
    }));
  });

  return out;
}

/** ========= Component ========= */
const Dashboard = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [isEditMode, setIsEditMode] = useState(false);

  // ✅ مثال ۵ ویجتی (بعداً با API جایگزین کن)



  const [widgets] = useState<Widget[]>([
    { id: "kpi_profit", title:<> PiChart</>,content:<><PiChart ></PiChart></>, defaultW: 12, defaultH: 3 },
    { id: "chart_sales", title:<>PiChart2 </>,content:<><PiChart2/></>, defaultW: 12, defaultH: 3 },
    { id: "list_tasks", title: <>ColumnChart1 </>,content:<><ColumnChart1/></>, defaultW: 12, defaultH: 3 },
    { id: "notes", title: <>DualAxesChart </>, content:<><DualAxesChart/></>,defaultW: 12, defaultH: 3 },
    { id: "alerts", title: <>DualAxesChart2 </>,content:<><DualAxesChart2/></>, defaultW: 12, defaultH: 3 },
    { id: "alerts2", title: <> FunnelChart</>,content:<><FunnelChart/></>, defaultW: 12, defaultH: 3 },
    { id: "alerts3", title: <>RadarChart</>, content:<><RadarChart/></>,defaultW: 12, defaultH: 3 },
    { id: "alerts4", title: <>TreemapChart</>, content:<><TreemapChart/></>,defaultW: 12, defaultH: 3 },
    { id: "alerts5", title: <>GaugeChart</>, content:<><GaugeChart/></>,defaultW: 12, defaultH: 3 },
    { id: "alerts6", title: <>BaseChart</>, content:<><BaseChart/></>,defaultW: 12, defaultH: 3 },

    // { id: "alerts7", title: <>OrganizationChart</>, content:<><OrganizationTChart/></>,defaultW: 12, defaultH: 3 },
  ]);

  const STORAGE_KEY = "dashboard-layout";

  const defaults = useMemo(() => buildDefaults(widgets), [widgets]);

  const [layouts, setLayouts] = useState<RGLLayouts>(() => {
    const saved = safeRead(STORAGE_KEY);
    return mergeLayouts(saved, defaults, widgets);
  });

  // همیشه ref آخرین layout رو نگه می‌داره
  const layoutsRef = useRef<RGLLayouts>(layouts);
  useEffect(() => {
    layoutsRef.current = layouts;
  }, [layouts]);

  // اگر ویجت‌ها عوض بشن (API)، مرج کن
  useEffect(() => {
    const saved = safeRead(STORAGE_KEY);
    setLayouts(mergeLayouts(saved, defaults, widgets));
  }, [defaults, widgets]);

  // ✅ ذخیره قطعی: وقتی edit mode خاموش شد، یک بار نهایی ذخیره کن
  useEffect(() => {
    if (!isEditMode) {
      safeWrite(STORAGE_KEY, layoutsRef.current);
    }
  }, [isEditMode]);

  const { ref, width } = useContainerWidth();

  const saveNow = (all?: RGLLayouts) => {
    const data = all ?? layoutsRef.current;
    safeWrite(STORAGE_KEY, data);
    // برای دیباگ:
    // console.log("SAVED", data);
  };

  const onLayoutChange = (_current: RGLLayout, all: RGLLayouts) => {
    setLayouts(all);
    // ✅ همزمان ذخیره کن (بدون شرط)
    saveNow(all);
  };

  // ✅ ذخیره قطعی روی stop (اگر onLayoutChange به هر دلیل کم فایر شد)
  const onDragStop = () => saveNow();
  const onResizeStop = () => saveNow();

  const resetLayout = () => {
    setLayouts(defaults);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      {/* <FloatButton
        type="primary"
        onClick={() => setIsEditMode((p) => !p)}
        style={{ right: 24, bottom: 24 }}
        description={isEditMode ? "پایان ویرایش" : "ویرایش چیدمان"}
        shape="square"
      />
      <FloatButton
        onClick={resetLayout}
        style={{ right: 24, bottom: 90 }}
        description="ریست چیدمان"
        shape="square"
      /> */}

      <div ref={ref} style={{ background: "#f5f5f5", padding: 8 , direction:'ltr'}}>
        <ResponsiveRGL
          width={width}
          className="layout"
          layouts={layouts}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={100}
          onLayoutChange={onLayoutChange}
          onDragStop={onDragStop}
          onResizeStop={onResizeStop}
          isDraggable={!isMobile && isEditMode}
          isResizable={!isMobile && isEditMode}
          preventCollision={true}
          compactType={isEditMode ? null : "vertical"}
         draggableHandle=".win__header"
draggableCancel=".win__fs-btn"
          resizeHandles={["e", "w"]}
        >
         {widgets.map((w) => (
  <div
    key={w.id}
    style={{
      height: "100%",
      width: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: 10,
      overflow: "hidden",
      background: "#fff",
    }}
  >
    <Window footerText={w.title}>
    {w.content}
    </Window>
  </div>
))}
        </ResponsiveRGL>
      </div>
    </>
  );
};

export default Dashboard;